import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';

import { DateAdapter } from '../../client/helpers/serializer/adapters/date.adapter';
import { DocumentProduct } from './document-product';
import { Id } from './id';
import { IdAdapter } from '../../client/helpers/serializer/adapters/id.adapter';
import { ProductInterface, ProductSpecification, RiskLevel } from '../../../core';
import { ProductSpecificationAdapter } from '../../client/helpers/serializer/adapters/product-specification.adapter';
import { RiskLevelAdapter } from '../../client/helpers/serializer/adapters/risk-level.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';

@JsonObject('Product')
export class Product implements ProductInterface {
    @JsonProperty('Id', IdAdapter, true)
    private _id: Id = undefined;

    @JsonProperty('ProductCode', IdAdapter, true)
    private _code: Id = undefined;

    @JsonProperty('Code', IdAdapter, true)
    private _encryptedCode: Id = undefined;

    @JsonProperty('Isin', StringAdapter, true)
    private _isin: string = undefined;

    @JsonProperty('IBAN', StringAdapter, true)
    private _iban: string = undefined;

    @JsonProperty('CNMV', StringAdapter, true)
    private _cnmv: string = undefined;

    @JsonProperty('Name', StringAdapter, true)
    private _name: string = undefined;

    @JsonProperty('Description', StringAdapter, true)
    private _description: string = undefined;

    @JsonProperty('RiskLevel', RiskLevelAdapter, true)
    private _riskLevel: RiskLevel = undefined;

    @JsonProperty('ProductSpecificationId', ProductSpecificationAdapter, true)
    private _productSpecification: ProductSpecification = undefined;

    @JsonProperty('ManagementFee', Number, true)
    private _managementFee: number = undefined;

    @JsonProperty('LastPrice', Number, true)
    private _lastPrice: number = undefined;

    @JsonProperty('LastPriceDate', DateAdapter, true)
    private _lastPriceDate: Moment = undefined;

    @JsonProperty('AssetClass', StringAdapter, true)
    private _assetClass: string = undefined;

    @JsonProperty('Liquidity', StringAdapter, true)
    private _liquidity: string = undefined;

    @JsonProperty('LiquidityDescription', StringAdapter, true)
    private _liquidityDescription: string = undefined;

    @JsonProperty('MinimumAmountToMaintain', Number, true)
    private _minimumAmountToMaintain: number = undefined;

    @JsonProperty('MinimumContribution', Number, true)
    private _minimumContribution: number = undefined;

    @JsonProperty('MinimunInvestment', Number, true)
    private _minimumInvestment: number = undefined;

    @JsonProperty('CustodyFee', Number, true)
    private _custodyFee: number = undefined;

    @JsonProperty('Custodian', StringAdapter, true)
    private _custodian: string = undefined;

    @JsonProperty('Documents', DocumentProduct, true)
    private _documents: DocumentProduct = undefined;

    @JsonProperty('ConvenienceGroupId', Number, true)
    private _convenienceGroup: number = undefined;

    @JsonProperty('FundAmmount', Number, true)
    private _amount: number = undefined;

    @JsonProperty('FundShares', Number, true)
    private _shares: number = undefined;

    private _selected = false;

    @JsonProperty('BuyAndHold', Boolean, true)
    private _buyAndHold = false;

    get id(): Id {
        return this._id;
    }

    get buyAndHold(): boolean {
        return this._buyAndHold;
    }

    get code(): Id {
        return this._code;
    }

    get encryptedCode(): Id {
        return this._encryptedCode;
    }

    get isin(): string {
        return this._isin;
    }

    get iban(): string {
        return this._iban;
    }

    get cnmv(): string {
        return this._cnmv;
    }

    get name(): string {
        return this._name;
    }

    get description(): string {
        return this._description;
    }

    get riskLevel(): RiskLevel {
        return this._riskLevel;
    }

    get productSpecification(): ProductSpecification {
        return this._productSpecification;
    }

    get managementFee(): number {
        return this._managementFee;
    }

    get lastPrice(): number {
        return this._lastPrice;
    }

    get lastPriceDate(): Moment {
        return this._lastPriceDate;
    }

    get assetClass(): string {
        return this._assetClass;
    }

    get liquidity(): string {
        return this._liquidity;
    }

    get liquidityDescription(): string {
        return this._liquidityDescription;
    }

    get minimumAmountToMaintain(): number {
        return this._minimumAmountToMaintain ? Number(this._minimumAmountToMaintain.toFixed(2)) : 0;
    }

    get minimumContribution(): number {
        return this._minimumContribution ? Number(this._minimumContribution.toFixed(2)) : 0;
    }

    get minimumInvestment(): number {
        return this._minimumInvestment ? Number(this._minimumInvestment.toFixed(2)) : 0;
    }

    get custodyFee(): number {
        return this._custodyFee;
    }

    get custodian(): string {
        return this._custodian;
    }

    get documents(): DocumentProduct {
        return this._documents;
    }

    get convenienceGroup(): number {
        return this._convenienceGroup;
    }

    get amount(): number {
        return this._amount || 0;
    }

    get shares(): number {
        return this._shares || 0;
    }

    get isAppropriateness(): boolean {
        return false;
    }

    get isAutomatic(): boolean {
        return false;
    }

    get isExonerated(): boolean {
        return false;
    }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        this._selected = value;
    }
}
